import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../lib/confirmDialog';
import RichTextEditor from '../components/wiki/RichTextEditor';

/**
 * WikiEditor - WYSIWYG editor for team wiki
 * Allows team managers to directly edit wiki sections and items with a template-like experience
 * Pro tier only
 */
export default function WikiEditor() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teamId, setTeamId] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);

  // Section configs with icons and ordering
  const [sectionConfigs, setSectionConfigs] = useState([]);

  // Wiki items grouped by section
  const [sections, setSections] = useState([]);

  // Track changes for manual save
  const [hasChanges, setHasChanges] = useState(false);

  // Editing states
  const [editingSection, setEditingSection] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [showAddSection, setShowAddSection] = useState(false);

  // Emoji picker state
  const [editingSectionIcon, setEditingSectionIcon] = useState(null);

  // Common emojis for sections
  const commonEmojis = ['📄', '📋', '📌', '🏆', '📅', '📍', '📞', '✉️', '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🎯', '📝', '💼', '👥', '🏃', '🎓'];

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in to edit the wiki');
        navigate('/login');
        return;
      }

      // Get team info and check Pro tier
      const { data: team, error: teamError } = await supabase
        .from('teams')
        .select('id, name, season_tier')
        .eq('user_id', user.id)
        .single();

      if (teamError || !team) {
        console.error('Error fetching team:', teamError);
        toast.error('No team found');
        navigate('/dashboard');
        return;
      }

      const tier = team.season_tier;
      if (tier !== 'paid') {
        toast.error('Wiki editing requires paid tier');
        navigate('/wiki');
        return;
      }

      setTeamId(team.id);
      setTeamInfo(team);
      await loadWikiData(team.id);
    } catch (error) {
      console.error('Error checking access:', error);
      toast.error('Error loading wiki editor');
      navigate('/wiki');
    }
  };

  const loadWikiData = async (tid) => {
    try {
      // Load section configs
      const { data: configs, error: configError } = await supabase
        .from('wiki_section_config')
        .select('*')
        .eq('team_id', tid)
        .order('sort_order', { ascending: true });

      if (configError) throw configError;

      // Load wiki items
      const { data: items, error: itemsError } = await supabase
        .from('wiki_items')
        .select('*')
        .eq('team_id', tid)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (itemsError) throw itemsError;

      // Group items by category
      const grouped = {};
      items.forEach(item => {
        const category = item.category || 'Quick Notes';
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(item);
      });

      // Merge configs with items
      const sectionsData = configs.map(config => ({
        ...config,
        items: grouped[config.category_name] || []
      }));

      // Add any categories that don't have configs yet
      Object.keys(grouped).forEach(category => {
        if (!configs.find(c => c.category_name === category)) {
          sectionsData.push({
            category_name: category,
            icon: '📄',
            sort_order: configs.length,
            items: grouped[category] || [],
            isNew: true // Flag for items without config yet
          });
        }
      });

      setSectionConfigs(configs);
      setSections(sectionsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading wiki data:', error);
      toast.error('Error loading wiki');
      setLoading(false);
    }
  };

  const handleAddSection = async () => {
    if (!newSectionName.trim()) {
      toast.error('Section name is required');
      return;
    }

    // Check if section already exists
    if (sections.find(s => s.category_name.toLowerCase() === newSectionName.trim().toLowerCase())) {
      toast.error('Section already exists');
      return;
    }

    const newSection = {
      category_name: newSectionName.trim(),
      icon: '📄',
      sort_order: sections.length,
      items: [],
      isNew: true
    };

    setSections([...sections, newSection]);
    setNewSectionName('');
    setShowAddSection(false);
    setHasChanges(true);
    toast.success('Section added - click Save to commit');
  };

  const handleRenameSection = (sectionIndex, newName) => {
    if (!newName.trim()) {
      toast.error('Section name cannot be empty');
      return;
    }

    const updated = [...sections];
    updated[sectionIndex] = {
      ...updated[sectionIndex],
      category_name: newName.trim()
    };
    setSections(updated);
    setEditingSection(null);
    setHasChanges(true);
  };

  const handleDeleteSection = async (sectionIndex) => {
    const section = sections[sectionIndex];

    if (section.items.length > 0) {
      const confirm = await confirmDialog(
        `Delete section "${section.category_name}"? This will move ${section.items.length} item(s) to "Quick Notes".`,
        { confirmText: 'Delete', cancelText: 'Cancel' }
      );
      if (!confirm) return;
    }

    // Move items to Quick Notes
    const itemsToMove = section.items;
    const updated = sections.filter((_, idx) => idx !== sectionIndex);

    // Add items to Quick Notes section or create it
    const quickNotesIdx = updated.findIndex(s => s.category_name === 'Quick Notes');
    if (quickNotesIdx >= 0) {
      updated[quickNotesIdx].items.push(...itemsToMove.map(item => ({
        ...item,
        category: 'Quick Notes'
      })));
    } else {
      updated.push({
        category_name: 'Quick Notes',
        icon: '📄',
        sort_order: updated.length,
        items: itemsToMove.map(item => ({
          ...item,
          category: 'Quick Notes'
        })),
        isNew: true
      });
    }

    setSections(updated);
    setHasChanges(true);
    toast.success('Section deleted - click Save to commit');
  };

  const handleMoveSectionUp = (sectionIndex) => {
    if (sectionIndex === 0) return;

    const updated = [...sections];
    [updated[sectionIndex - 1], updated[sectionIndex]] = [updated[sectionIndex], updated[sectionIndex - 1]];

    // Update sort orders
    updated.forEach((section, idx) => {
      section.sort_order = idx;
    });

    setSections(updated);
    setHasChanges(true);
  };

  const handleMoveSectionDown = (sectionIndex) => {
    if (sectionIndex === sections.length - 1) return;

    const updated = [...sections];
    [updated[sectionIndex], updated[sectionIndex + 1]] = [updated[sectionIndex + 1], updated[sectionIndex]];

    // Update sort orders
    updated.forEach((section, idx) => {
      section.sort_order = idx;
    });

    setSections(updated);
    setHasChanges(true);
  };

  const handleUpdateSectionIcon = (sectionIndex, icon) => {
    const updated = [...sections];
    updated[sectionIndex] = {
      ...updated[sectionIndex],
      icon
    };
    setSections(updated);
    setEditingSectionIcon(null);
    setHasChanges(true);
  };

  const handleAddItem = (sectionIndex) => {
    const section = sections[sectionIndex];
    const newItem = {
      id: `temp-${Date.now()}`,
      title: '',
      content: '',
      type: 'note',
      category: section.category_name,
      source: 'manual',
      is_pinned: false,
      isNew: true,
      isEditing: true
    };

    const updated = [...sections];
    updated[sectionIndex].items = [newItem, ...updated[sectionIndex].items];
    setSections(updated);
    setEditingItem({ sectionIndex, itemIndex: 0 });
    setHasChanges(true);
  };

  const handleUpdateItem = (sectionIndex, itemIndex, field, value) => {
    const updated = [...sections];
    updated[sectionIndex].items[itemIndex] = {
      ...updated[sectionIndex].items[itemIndex],
      [field]: value
    };
    setSections(updated);
    setHasChanges(true);
  };

  const handleDeleteItem = async (sectionIndex, itemIndex) => {
    const item = sections[sectionIndex].items[itemIndex];
    const confirm = await confirmDialog(`Delete "${item.title || 'Untitled'}"?`, {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirm) return;

    const updated = [...sections];
    updated[sectionIndex].items = updated[sectionIndex].items.filter((_, idx) => idx !== itemIndex);
    setSections(updated);
    setHasChanges(true);
    toast.success('Item deleted - click Save to commit');
  };

  const handleSaveAll = async () => {
    if (!hasChanges) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      // 1. Update or insert section configs
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i];

        const { error: configError } = await supabase
          .from('wiki_section_config')
          .upsert({
            team_id: teamId,
            category_name: section.category_name,
            icon: section.icon,
            sort_order: i
          }, {
            onConflict: 'team_id,category_name'
          });

        if (configError) throw configError;
      }

      // 2. Update or insert wiki items
      for (const section of sections) {
        for (const item of section.items) {
          if (item.id.startsWith('temp-')) {
            // New item - insert
            const { error: insertError } = await supabase
              .from('wiki_items')
              .insert({
                team_id: teamId,
                title: item.title || 'Untitled',
                content: item.content || '',
                type: item.type,
                category: section.category_name,
                source: 'manual',
                is_pinned: item.is_pinned || false
              });

            if (insertError) throw insertError;
          } else {
            // Existing item - update
            const { error: updateError } = await supabase
              .from('wiki_items')
              .update({
                title: item.title,
                content: item.content,
                type: item.type,
                category: section.category_name,
                is_pinned: item.is_pinned
              })
              .eq('id', item.id);

            if (updateError) throw updateError;
          }
        }
      }

      // 3. Delete section configs that no longer exist
      const currentCategories = sections.map(s => s.category_name);
      const { error: deleteConfigError } = await supabase
        .from('wiki_section_config')
        .delete()
        .eq('team_id', teamId)
        .not('category_name', 'in', `(${currentCategories.map(c => `'${c}'`).join(',')})`);

      if (deleteConfigError) console.warn('Error deleting old configs:', deleteConfigError);

      toast.success('Wiki saved successfully!');
      setHasChanges(false);

      // Reload data
      await loadWikiData(teamId);
    } catch (error) {
      console.error('Error saving wiki:', error);
      toast.error('Error saving wiki: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (hasChanges) {
      const confirm = await confirmDialog('You have unsaved changes. Are you sure you want to leave?', {
        confirmText: 'Leave',
        cancelText: 'Stay'
      });
      if (!confirm) return;
    }
    navigate('/wiki');
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Edit Wiki</h1>
            <p className="text-sm text-gray-600 mt-1">
              {teamInfo?.name} - Template-based editing
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAll}
              disabled={!hasChanges || saving}
              className={`px-4 py-2 text-white rounded-lg font-medium ${
                hasChanges && !saving
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
            </button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            You have unsaved changes. Click "Save Changes" to commit your edits.
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="bg-white border border-gray-200 rounded-lg shadow-sm">
            {/* Section Header */}
            <div className="bg-gray-50 border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  {/* Icon */}
                  <div className="relative">
                    <button
                      onClick={() => setEditingSectionIcon(editingSectionIcon === sectionIndex ? null : sectionIndex)}
                      className="text-2xl hover:bg-gray-100 rounded p-1"
                      title="Change icon"
                    >
                      {section.icon}
                    </button>

                    {editingSectionIcon === sectionIndex && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2 z-10 grid grid-cols-5 gap-1">
                        {commonEmojis.map(emoji => (
                          <button
                            key={emoji}
                            onClick={() => handleUpdateSectionIcon(sectionIndex, emoji)}
                            className="text-2xl hover:bg-gray-100 rounded p-1"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  {editingSection === sectionIndex ? (
                    <input
                      type="text"
                      defaultValue={section.category_name}
                      onBlur={(e) => handleRenameSection(sectionIndex, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSection(sectionIndex, e.target.value);
                        } else if (e.key === 'Escape') {
                          setEditingSection(null);
                        }
                      }}
                      autoFocus
                      className="flex-1 px-2 py-1 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <h2
                      onClick={() => setEditingSection(sectionIndex)}
                      className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                      title="Click to rename"
                    >
                      {section.category_name}
                    </h2>
                  )}

                  <span className="text-sm text-gray-500">
                    ({section.items.length} {section.items.length === 1 ? 'item' : 'items'})
                  </span>
                </div>

                {/* Section Controls */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleMoveSectionUp(sectionIndex)}
                    disabled={sectionIndex === 0}
                    className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleMoveSectionDown(sectionIndex)}
                    disabled={sectionIndex === sections.length - 1}
                    className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => handleAddItem(sectionIndex)}
                    className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                    title="Add item"
                  >
                    + Add Item
                  </button>
                  <button
                    onClick={() => handleDeleteSection(sectionIndex)}
                    className="p-1 text-red-600 hover:text-red-800"
                    title="Delete section"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>

            {/* Section Items */}
            <div className="p-4 space-y-4">
              {section.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No items in this section</p>
                  <button
                    onClick={() => handleAddItem(sectionIndex)}
                    className="mt-2 text-blue-600 hover:underline"
                  >
                    Add your first item
                  </button>
                </div>
              ) : (
                section.items.map((item, itemIndex) => (
                  <div
                    key={item.id}
                    className={`border rounded-lg p-4 ${
                      editingItem?.sectionIndex === sectionIndex && editingItem?.itemIndex === itemIndex
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    {editingItem?.sectionIndex === sectionIndex && editingItem?.itemIndex === itemIndex ? (
                      // Edit Mode
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Title
                            </label>
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) => handleUpdateItem(sectionIndex, itemIndex, 'title', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Item title"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Type
                            </label>
                            <select
                              value={item.type}
                              onChange={(e) => handleUpdateItem(sectionIndex, itemIndex, 'type', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="note">Note</option>
                              <option value="link">Link</option>
                              <option value="address">Address</option>
                              <option value="phone">Phone</option>
                              <option value="document">Document</option>
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Content
                          </label>
                          <RichTextEditor
                            content={item.content}
                            onChange={(html) => handleUpdateItem(sectionIndex, itemIndex, 'content', html)}
                            placeholder="Add content..."
                            teamId={teamId}
                          />
                        </div>

                        <div className="flex items-center gap-3">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.is_pinned || false}
                              onChange={(e) => handleUpdateItem(sectionIndex, itemIndex, 'is_pinned', e.target.checked)}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Pin to top</span>
                          </label>
                        </div>

                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setEditingItem(null)}
                            className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{item.title || 'Untitled'}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 capitalize">{item.type}</span>
                              {item.is_pinned && (
                                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                  Pinned
                                </span>
                              )}
                              {item.source === 'ai_extracted' && (
                                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                                  AI
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingItem({ sectionIndex, itemIndex })}
                              className="text-sm text-blue-600 hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(sectionIndex, itemIndex)}
                              className="text-sm text-red-600 hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                        <div
                          className="prose prose-sm max-w-none text-gray-700"
                          dangerouslySetInnerHTML={{ __html: item.content }}
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))}

        {/* Add Section Button */}
        {showAddSection ? (
          <div className="bg-white border border-blue-300 rounded-lg p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddSection();
                  if (e.key === 'Escape') {
                    setShowAddSection(false);
                    setNewSectionName('');
                  }
                }}
                placeholder="Section name (e.g., Tournament Info)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
              <button
                onClick={handleAddSection}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddSection(false);
                  setNewSectionName('');
                }}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddSection(true)}
            className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
          >
            + Add New Section
          </button>
        )}
      </div>

      {/* Sticky Save Bar (mobile) */}
      {hasChanges && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:hidden shadow-lg">
          <button
            onClick={handleSaveAll}
            disabled={saving}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}
    </div>
  );
}
